import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import {
  NOTIFICATION_LEVEL_ERROR,
  NOTIFICATION_LEVEL_SUCCESS,
} from '@avtechsoftware/ra-javascript-utilities/src/types/NotificationLevels';

import { getNextNumericalName } from '@avtechsoftware/ra-javascript-utilities/src/utilities/validation/ObjectListUtils';
import AxiosDataWrapper
  from '@avtechsoftware/react-component-library/src/components/common/AxiosDataWrapper/AxiosDataWrapper';
import AxiosDataIsLoaded
  from '@avtechsoftware/react-component-library/src/components/common/AxiosDataWrapper/AxiosDataIsLoaded/AxiosDataIsLoaded';
import {
  createNotificationGroup,
  fetchNotificationGroups,
  selectNotificationGroupList,
  selectNotificationGroupsCrudOperation,
  selectNotificationGroupsCrudStatus,
  setNotificationGroupsCrudOperation,
} from '../../../../redux/slices/notificationGroupsSlice/notificationGroupsSlice';
import { pushSessionMessage } from '../../../../redux/slices/toastNotificationSlice/toastNotificationSlice';

import { CrudType } from '../../../../utility/types/CrudType';
import { CrudStatus } from '../../../../utility/types/CrudStatus';
import NotificationGroupFormInner
  from '../../../alert_actions/notificationGroups/NotificationGroupForm/NotificationGroupFormInner';
import useNotificationGroupForm
  from '../../../alert_actions/notificationGroups/NotificationGroupForm/useNotificationGroupForm';
import { selectActionsData, updateStepData } from '../../../../redux/slices/alertFormSlice/alertFormSlice';

function NewNotificationGroupModal(props) {
  const {
    show,
    handleClose,
    actionIdToApplyGroup = null,
    onCreateNewNotificationGroup,
  } = props;

  const dispatch = useDispatch();
  const crudOperation = useSelector(selectNotificationGroupsCrudOperation);
  const crudStatus = useSelector(selectNotificationGroupsCrudStatus);
  const notificationGroups = useSelector(selectNotificationGroupList);
  const { actions } = useSelector(selectActionsData);

  const [isFormSubmitting, setIsFormSubmitting] = useState(false);
  const [submitBtnText, setSubmitBtnText] = useState('Save');

  const {
    notificationGroup,
    errorList,
    submittable,
    validateRecipients,
    handleRecipientChange,
    addRecipientRow,
    onDeleteBtnPress,
    onNameChange,
    mapNotificationGroupForSave,
    resetForm,
  } = useNotificationGroupForm({ name: '', recipients: [] });

  const handleSuccess = (message) => {
    dispatch(pushSessionMessage({
      level: NOTIFICATION_LEVEL_SUCCESS,
      title: 'Saved',
      body: message,
    }));
    dispatch(setNotificationGroupsCrudOperation(CrudType.READ));
    setIsFormSubmitting(false);
    setSubmitBtnText('Save');
    handleClose();
  };

  const handleFailure = (title, message) => {
    dispatch(fetchNotificationGroups());
    dispatch(pushSessionMessage({
      level: NOTIFICATION_LEVEL_ERROR,
      title,
      body: message,
    }));
    setIsFormSubmitting(false);
    setSubmitBtnText('Save');
  };

  useEffect(() => {
    if (crudOperation === CrudType.CREATE) {
      if (crudStatus === CrudStatus.SUCCESS) {
        handleSuccess('Notification group saved successfully.');
      } else if (crudStatus === CrudStatus.ERROR) {
        handleFailure('Save Error', 'Error saving notification group.');
      }
    }
  }, [crudOperation, crudStatus]);

  useEffect(() => {
    if (show) {
      resetForm({
        name: getNextNumericalName(notificationGroups, 'New Notification Group', 'name'),
        recipients: [{
          id: 0,
          transportId: '0',
          context: '',
          userId: 0,
        }],
      });
      setIsFormSubmitting(false);
      setSubmitBtnText('Save');
    }
  }, [show]);

  const handleSave = async (e) => {
    e.preventDefault();
    setIsFormSubmitting(true);
    setSubmitBtnText('Saving...');

    const areRecipientsValid = validateRecipients(notificationGroup.recipients);

    if (!areRecipientsValid.includes(false)) {
      await dispatch(createNotificationGroup(mapNotificationGroupForSave())).unwrap()
        .then((res) => {
          const newGroupId = res.notificationGroup.id;

          if (actionIdToApplyGroup && newGroupId) {
            const updatedActions = actions.map((action) => {
              if (action.id !== actionIdToApplyGroup) return action;

              const currentIds = Array.isArray(action.notificationGroupIdList)
                ? action.notificationGroupIdList
                : [];

              return {
                ...action,
                notificationGroupIdList: currentIds.includes(newGroupId)
                  ? currentIds
                  : [...currentIds, newGroupId],
              };
            });

            dispatch(updateStepData({
              step: 'DEFINE_ACTIONS',
              data: { actions: updatedActions },
            }));

            onCreateNewNotificationGroup(actionIdToApplyGroup);
          }
        })
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.error('Failed to save notification group:', err);
          setIsFormSubmitting(false);
          setSubmitBtnText('Save');
        });
    } else {
      setIsFormSubmitting(false);
      setSubmitBtnText('Save');
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="xl" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>New Notification Group</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {show && (
          <AxiosDataWrapper url="/subscription/fetch-scrubbed-user-list" config={{ dataTitle: 'users' }}>
            <AxiosDataIsLoaded>
              <NotificationGroupFormInner
                notificationGroup={notificationGroup}
                errorList={errorList}
                onNameChange={onNameChange}
                handleRecipientChange={handleRecipientChange}
                onDeleteBtnPress={onDeleteBtnPress}
                addRecipientRow={addRecipientRow}
              />
            </AxiosDataIsLoaded>
          </AxiosDataWrapper>
        )}
      </Modal.Body>

      <Modal.Footer>
        <button type="button" className="btn btn-secondary" onClick={handleClose}>Cancel</button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleSave}
          disabled={isFormSubmitting || !submittable}
        >
          {submitBtnText}
        </button>
      </Modal.Footer>
    </Modal>
  );
}

NewNotificationGroupModal.defaultProps = { actionIdToApplyGroup: null };
NewNotificationGroupModal.propTypes = {
  show: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  actionIdToApplyGroup: PropTypes.number,
  onCreateNewNotificationGroup: PropTypes.func.isRequired,
};

export default NewNotificationGroupModal;
